import React from "react";
import PropTypes from "prop-types";
import { Box, CircularProgress, Typography } from "@material-ui/core";
import BrokenImageIcon from "@material-ui/icons/BrokenImage";

import apiClient from "../../../../../../auth/apiClient";
import InstallationPhotoService from "../../../../../../services/InstallationPhotoService";
import { useInstallationImage } from "./useInstallationImage";
import { useStyles } from "./styles";

const service = new InstallationPhotoService(apiClient);

/**
 * A single thumbnail that lazily fetches its own image (base64) on demand, so
 * the list does not pull every full-resolution photo at once.
 */
const Thumbnail = ({ photo, onSelect }) => {
  const classes = useStyles();
  const { src, loading, error } = useInstallationImage(
    service,
    photo.deviceId,
    photo.installationImageId
  );

  return (
    <Box
      className={classes.thumbnail}
      role="button"
      tabIndex={0}
      onClick={() => onSelect(photo)}
      onKeyPress={(e) => {
        if (e.key === "Enter" || e.key === " ") onSelect(photo);
      }}
      data-testid="photo-thumbnail"
    >
      {loading && (
        <Box className={classes.thumbnailLoading} data-testid="thumbnail-loading">
          <CircularProgress size={20} />
        </Box>
      )}
      {!loading && error && (
        <Box className={classes.thumbnailLoading} data-testid="thumbnail-error">
          <BrokenImageIcon color="disabled" />
        </Box>
      )}
      {!loading && !error && src && (
        <img
          className={classes.thumbnailImage}
          src={src}
          alt="Installation thumbnail"
        />
      )}
    </Box>
  );
};

Thumbnail.propTypes = {
  photo: PropTypes.shape({
    installationImageId: PropTypes.string,
    deviceId: PropTypes.string,
  }).isRequired,
  onSelect: PropTypes.func.isRequired,
};

/**
 * Scrollable, newest-first list of installation photo thumbnails (Requirement 4).
 * The `photos` prop is expected to already be ordered newest-first by the API;
 * clicking a thumbnail opens the full-screen viewer.
 */
const PhotoList = ({ photos, onSelect }) => {
  const classes = useStyles();

  if (!photos || photos.length === 0) {
    return (
      <Box className={classes.emptyState} data-testid="photos-empty-state">
        <Typography variant="body2" color="textSecondary">
          No installation photos yet.
        </Typography>
      </Box>
    );
  }

  return (
    <Box className={classes.photoList} data-testid="photo-list">
      {photos.map((photo) => (
        <Thumbnail
          key={photo.installationImageId}
          photo={photo}
          onSelect={onSelect}
        />
      ))}
    </Box>
  );
};

PhotoList.propTypes = {
  photos: PropTypes.arrayOf(
    PropTypes.shape({
      installationImageId: PropTypes.string,
    })
  ),
  onSelect: PropTypes.func.isRequired,
};

PhotoList.defaultProps = {
  photos: [],
};

export default PhotoList;
